import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTrackLayout } from "@/hooks/useTrackLayout";
import { useTeamLiveTrail } from "@/hooks/useTeamLiveTrail";
import TrackLayoutVisualization from "@/components/tracks/TrackLayoutVisualization";
import LiveTrailMap from "@/components/tracks/LiveTrailMap";
import type { TeamLivePosition } from "@/types";

// Dim markers whose fix is older than this — the poll is 1s and devices flush
// ~1s, so anything older means the member's telemetry stalled (or they left).
const STALE_AFTER_MS = 10_000;

function markerLabel(position: TeamLivePosition): string {
  const given = position.member.givenName.trim();
  const family = position.member.familyName.trim();
  if (given && family) return `${given[0]}${family[0]}`.toUpperCase();
  return (given || family || "?").slice(0, 2).toUpperCase();
}

export interface TeamLiveMapPanelProps {
  positions: TeamLivePosition[];
  /** Clock for staleness dimming — the page passes React Query's dataUpdatedAt. */
  now: number;
  /** Auto-derived from the positions when omitted (members share a track). */
  trackId?: string | null;
  /** Team id — keys the persisted breadcrumb trail (sessionStorage). */
  teamId: string;
}

export default function TeamLiveMapPanel({
  positions,
  now,
  teamId,
}: TeamLiveMapPanelProps) {
  const { t } = useTranslation("pages");
  // Team sessions at one event share a track; fall back to the first entry's.
  const trackId = useMemo(
    () => positions.find((p) => p.trackId)?.trackId ?? null,
    [positions],
  );
  const { data: layout, isLoading } = useTrackLayout(trackId ?? "");
  const trailEnabled = !isLoading && !layout;
  const { segments } = useTeamLiveTrail(teamId, positions, trailEnabled);

  const markers = positions.map((p) => ({
    userId: p.member.userId,
    label: markerLabel(p),
    lat: p.lat,
    lng: p.lng,
    speedKmh: p.speed != null ? p.speed * 3.6 : null,
    stale: now - new Date(p.capturedAt).getTime() > STALE_AFTER_MS,
  }));

  return (
    <div
      data-testid="team-live-map-panel"
      className="flex h-full min-h-0 flex-col gap-3 p-4"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-200">
          {t("teamLive.mapTitle", "Track map")}
        </h2>
        <span className="text-xs text-slate-400">
          {t("teamLive.mapMemberCount", {
            defaultValue: "{{count}} on track",
            count: positions.length,
          })}
        </span>
      </div>
      <div className="min-h-0 flex-1">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-xs text-slate-500">
            {t("teamLive.mapLoading", "Loading track…")}
          </div>
        ) : layout ? (
          <TrackLayoutVisualization
            layoutData={layout}
            theme="dark"
            className="h-full w-full"
            showSectors={false}
            teamPositions={markers}
          />
        ) : segments.length > 0 || positions.length > 0 ? (
          <LiveTrailMap
            segments={segments}
            teamPositions={markers}
            className="h-full w-full"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-slate-500">
            {t("teamLive.mapNoLayout", "No track layout for this session")}
          </div>
        )}
      </div>
    </div>
  );
}
